import addConsultationToPartnerApi, {
	ConsultationRequest,
} from "@/lib/partner-api/addConsultation";
import {useRouter} from "next/navigation";
import {useCallback, useState} from "react";
import {
	ContactFormState,
	composeConsultationRequest,
	hasDuplicateChildren,
	hasSelectedChildren,
} from "./contactFormUtils";
import {useContactFormContext} from "@/app/_components/contexts/ContactFormContext";
import {useUserInfo} from "@/app/_components/contexts/userInfoContext";
import {useClientDeviceId} from "@/app/_components/contexts/ClientSourceTrackingContext";

type UseContactFormOptions = {
	successRedirectURL?: string;
};

export type UseContactForm = {
	isSubmitting: boolean;
	serverErrors: string[];
	submitForm: (formState: ContactFormState) => Promise<void>;
};

const pushDataLayerEvent = (consultationRequest: ConsultationRequest) => {
	const event = {
		event: "consultation_request",
		consultationRequest,
	};
	window.dataLayer?.push(event);
};

const getThankYouPageUrl = (
	{selectedCampuses: {primaryCampus, secondaryCampus}}: ContactFormState,
	options?: Partial<UseContactFormOptions>
): string | null => {
	if (!options?.successRedirectURL) {
		return null;
	}
	const query = new URLSearchParams({
		primary: primaryCampus.toString(),
	});
	if (secondaryCampus) {
		query.append("additional", secondaryCampus.toString());
	}
	return `${options.successRedirectURL}?${query}`;
};

const useContactForm: (
	options?: Partial<UseContactFormOptions>
) => UseContactForm = (options?: {successRedirectURL?: string}) => {
	// setup router for redirecting the user after successful form submission
	const router = useRouter();

	const contactFormContext = useContactFormContext();
	// TODO: we may need to handle the userInfo loading state in this function
	//      (i.e. block it from going through if the userInfo is loading)
	//      though right now I can't think of any problems not handling may pose
	const {userInfo} = useUserInfo();
	const clientDeviceId = useClientDeviceId();

	// setup form states
	const [isSubmitting, setSubmitting] = useState<boolean>(false);
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const clearForm = useCallback(() => {
		if (!contactFormContext) {
			return;
		}

		contactFormContext.parentFieldControls.resetParentFields();
		contactFormContext.studentFieldControls.resetStudentFields();
	}, [contactFormContext]);

	const submitForm = useCallback(
		async (formState: ContactFormState) => {
			if (isSubmitting) {
				return;
			}
			setServerErrors([]);
			setSubmitting(true);

			const consultationRequest = composeConsultationRequest(
				formState,
				userInfo,
				clientDeviceId,
				contactFormContext?.contactFormUserInfo.studentsByApp
			);

			if (!hasSelectedChildren(consultationRequest)) {
				setServerErrors(["Please select a child for consultation."]);
				setSubmitting(false);
				return;
			}

			if (hasDuplicateChildren(consultationRequest)) {
				setServerErrors(["Please remove duplicate student names"]);
				setSubmitting(false);
				return;
			}

			const res = await addConsultationToPartnerApi(consultationRequest);
			if (!res.success) {
				// TODO: handle field errors
				setServerErrors([res.message]);
				setSubmitting(false);
				return;
			}
			pushDataLayerEvent(consultationRequest);
			clearForm();
			contactFormContext?.refreshUserInfo?.();
			setSubmitting(false);

			const redirectUrl = getThankYouPageUrl(formState, options);
			if (redirectUrl) {
				router.push(redirectUrl);
			}
		},
		[
			clearForm,
			isSubmitting,
			options,
			router,
			userInfo,
			clientDeviceId,
			contactFormContext,
		]
	);

	return {
		isSubmitting,
		serverErrors,
		submitForm,
		clearForm,
	};
};

export default useContactForm;
